import { useState } from "react";
import { ShieldCheck, Clock, CheckCircle2, Mail } from "lucide-react";
import { Reveal, trackEvent } from "./Reveal";
import { CONTACT_EMAIL } from "@/config/site";

const AGES = ["12-13 ans", "14-15 ans", "16-17 ans", "18 ans et + (étudiant)"];
const GOALS = ["Manque de confiance / Repli", "Gestion du stress & scolaire", "Orientation / cap clair (études)", "Canaliser l'énergie / Rigueur", "Objectif sportif de combat", "Autre"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-obsidian/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-ember/60 focus:ring-2 focus:ring-ember/20";

const Contact = () => {
  const [form, setForm] = useState({ parent_name: "", phone: "", email: "", teen_age: "", goal: "", message: "" });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = `Demande de diagnostic offert — ${form.parent_name}`;
    const body = [
      `Nom & prénom : ${form.parent_name}`,
      `Téléphone : ${form.phone}`,
      `Adresse email : ${form.email}`,
      form.teen_age ? `Âge du jeune concerné : ${form.teen_age}` : null,
      form.goal ? `Motif principal : ${form.goal}` : null,
      "",
      form.message || "",
    ]
      .filter((l) => l !== null)
      .join("\n");
    trackEvent("contact_submit", { goal: form.goal || "non_precise" });
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  return (
    <section id="contact" className="py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-2 items-start">
        <Reveal>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-ember mb-4">Contact</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-slate-100 leading-tight">
            Un premier échange stratégique, offert et sans engagement
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg">
            Entretien téléphonique ou visio de 30 minutes. Faisons le point ensemble sur la
            situation, les blocages et la solution la plus adaptée — que vous soyez parent,
            ado ou étudiant.
          </p>
          <ul className="mt-9 space-y-5">
            {[
              { icon: ShieldCheck, text: "Confidentialité absolue" },
              { icon: Clock, text: "Réponse sous 24h par Yassine" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3.5">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-ember/12 border border-ember/30 shrink-0">
                  <Icon size={17} className="text-ember" />
                </span>
                <span className="text-sm sm:text-base font-medium text-slate-200">{text}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-white/10 bg-coal p-7 sm:p-9 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
            {sent ? (
              <div className="py-14 text-center" data-testid="contact-success-toast">
                <CheckCircle2 size={52} className="mx-auto text-gold" />
                <h3 className="mt-6 font-heading text-2xl font-extrabold uppercase text-slate-100">Votre message est prêt</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-sm mx-auto leading-relaxed">
                  Votre messagerie vient de s'ouvrir avec votre demande pré-remplie — il ne vous
                  reste qu'à l'envoyer. Yassine vous recontacte personnellement sous 24h.
                </p>
                <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                  Rien ne s'est ouvert ? Écrivez directement à{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-fallback-link" className="text-gold underline underline-offset-2 hover:text-amber-400 transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label htmlFor="contact-parent-name" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                    Nom & prénom *
                  </label>
                  <input id="contact-parent-name" data-testid="contact-parent-name-input" required value={form.parent_name} onChange={set("parent_name")} className={inputCls} placeholder="Ex. Sophie Martin" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-phone" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Téléphone *
                    </label>
                    <input id="contact-phone" data-testid="contact-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="06 12 34 56 78" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Adresse email *
                    </label>
                    <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="vous@exemple.fr" />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-age" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Âge du jeune concerné
                    </label>
                    <select id="contact-age" data-testid="contact-age-select" value={form.teen_age} onChange={set("teen_age")} className={inputCls}>
                      <option value="">Sélectionner…</option>
                      {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-goal" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Motif principal
                    </label>
                    <select id="contact-goal" data-testid="contact-goal-select" value={form.goal} onChange={set("goal")} className={inputCls}>
                      <option value="">Sélectionner…</option>
                      {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                    Quelques mots sur la situation
                  </label>
                  <textarea id="contact-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Décrivez en quelques lignes la situation et ce que vous en attendez…" />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ember hover:bg-emberdark px-7 py-4 text-sm font-bold uppercase tracking-wide transition-colors duration-200"
                >
                  <Mail size={17} />
                  Envoyer ma demande
                </button>
                <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                  Le bouton ouvre votre messagerie avec le message pré-rempli, adressé à {CONTACT_EMAIL}.
                  Vos données restent strictement confidentielles.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
